import { useLayoutEffect, useMemo } from "react"

import useSWRInfinite, { SWRInfiniteConfiguration } from "swr/infinite"

import { GeneratedQuery } from "@/types/GQLTypes"

export const makeGetKey =
  <Q extends GeneratedQuery<any, any>>(
    queryKey: string,
    query: Q | null,
    queryVariables?: Q["__generatedQueryInput"]
  ) =>
  (_i: number, previousPageData: Q["__generatedQueryOutput"] | null): null | [string, Record<string, any>] => {
    if (!query || (previousPageData && !previousPageData[queryKey]?.nextToken)) {
      return null
    }
    return [
      query,
      {
        ...queryVariables,
        nextToken: previousPageData?.[queryKey]?.nextToken,
        // limit: 10,
      },
    ]
  }

export default function useQueryPaged<
  T extends any[] = any[],
  Q extends GeneratedQuery<any, any> = GeneratedQuery<any, any>,
>(
  queryKey: string,
  query: Q | null,
  queryVariables?: Q["__generatedQueryInput"],
  swrOptions?: SWRInfiniteConfiguration
) {
  const getKey = makeGetKey(queryKey, query, queryVariables)

  const { data, isLoading, setSize, ...swrMembers } = useSWRInfinite<Q["__generatedQueryOutput"]>(getKey, swrOptions)

  // Keep fetching until there are no more pages
  useLayoutEffect(() => {
    if (isLoading) {
      return
    }
    if (data && data.length > 0 && data[data.length - 1]?.[queryKey]?.nextToken) {
      setSize((i) => i + 1)
    }
  }, [data, isLoading, queryKey, setSize])

  const flattenedData = useMemo<T>(
    () => data?.flatMap((d) => d?.[queryKey]?.items || []) || ([] as any),
    [data, queryKey]
  )

  return { data: flattenedData, isLoading, ...swrMembers }
}
